/**
 * @license Copyright (c) 2014-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor.js';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';
import Emoji from '@ckeditor/ckeditor5-emoji/src/emoji.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace.js';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor.js';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily.js';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize.js';
import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight.js';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline.js';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import Mention from '@ckeditor/ckeditor5-mention/src/mention.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';
import ImageInsert from '@ckeditor/ckeditor5-image/src/imageinsert.js';
import Indent from '@ckeditor/ckeditor5-indent/src/indent.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import List from '@ckeditor/ckeditor5-list/src/list.js';
// import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize.js';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter.js';
import ResizableHeight from '@pikulinpw/ckeditor5-resizableheight';
import FullScreen from '@ckeditor/ckeditor5-fullscreen/src/fullscreen';
import {PageBreak} from "@ckeditor/ckeditor5-page-break";
import {ShowBlocks} from "@ckeditor/ckeditor5-show-blocks";

// UI components - these typically use named exports
import { ButtonView } from '@ckeditor/ckeditor5-ui';
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import { addListToDropdown, createDropdown } from '@ckeditor/ckeditor5-ui/src/dropdown/utils';
import Collection from '@ckeditor/ckeditor5-utils/src/collection';
import Model from '@ckeditor/ckeditor5-ui/src/model';
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';

import iconPdf from '../src/pdf.svg';

import axios from 'axios';


class DownloadPdf extends Plugin {
    static get pluginName() {
        return 'DownloadPdf';
    }

    init() {
        const editor = this.editor;
        editor.ui.componentFactory.add( 'downloadPdf', () => {
            const button = new ButtonView();
            button.set( {
                label: 'Download PDF',
                withText: false,
                'icon': iconPdf,
            } );

            button.on( 'execute', () => {
                button.isEnabled = false;
                axios.defaults.withCredentials = true;
                // PDF generation is asynchronous: the request only dispatches the job
                // (via SNS -> Lambda) and returns JSON {filename, uuid}. The X-Requested-With
                // header is required so the backend treats this as ajax and returns that JSON
                // instead of redirecting. We then register the job with the global TaskManager
                // so a pending PDF shows up immediately in the Task Monitor, and restart polling
                // so the row gets a download link once the Lambda finishes.
                axios({
                    url: '/pdf/index',
                    data: {content: editor.getData()},
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': document.getElementsByTagName('meta')['csrfToken'].getAttribute('content'),
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                    }
                }).then((response) => {
                    const data = response.data || {};

                    if (window.TaskManager && data.uuid) {
                        window.TaskManager.addPending({
                            uuid: data.uuid,
                            task_type: 'pdfgen',
                            filename: data.filename,
                        });

                        // Kick the offloaded-tasks poller so it picks up this job and
                        // resolves the row to a completed download (or failure) state.
                        if (typeof window.stopPolling === 'function') {
                            window.stopPolling();
                        }
                        setTimeout(() => {
                            if (typeof window.startPolling === 'function') {
                                window.startPolling();
                            }
                        }, 5000);
                    }

                    button.isEnabled = true;
                }).catch((error) => {
                    console.error('PDF generation failed:', error);
                    button.isEnabled = true;
                });
            });

            return button;
        });
    }
}
class PlaceholderInserter extends Plugin {
    static get pluginName() {
        return 'PlaceholderInserter';
    }
    init() {
        const editor = this.editor;

		editor.commands.add('insertSelfLink', {
            execute: () => {
                const insertPlaceholder = '{{self_link_start}}your link text here{{self_link_end}}';
                const selection = editor.model.document.selection;
                let insertedTextPosition;

                editor.model.change(writer => {
                    const insertPosition = selection.getFirstPosition();

                    // Insert the placeholder at the specified position.
                    writer.insertText(insertPlaceholder, insertPosition);

                    // Store the inserted text position.
                    insertedTextPosition = insertPosition.getShiftedBy(0); // Adjust the position.
                });

                // Set the selection to the range after giving some time for the view to update.
                setTimeout(() => {
                    editor.model.change(writer => {
                        // Specify start and end positions for selection
                        let start = 19;
                        let end = 38;

                        // Create a selection range.
                        const rangeStart = insertedTextPosition.getShiftedBy(start);
                        const rangeEnd = insertedTextPosition.getShiftedBy(end);
                        const range = writer.createRange(rangeStart, rangeEnd);

                        writer.setSelection(range);
                    });
                }, 0);
            }
        });

        editor.commands.add('insertSelf', {
            execute: () => {
                const insertPlaceholder = '{{self}}';
                const selection = editor.model.document.selection;
                let insertedTextPosition;

                editor.model.change(writer => {
                    const insertPosition = selection.getFirstPosition();

                    // Insert the placeholder at the specified position.
                    writer.insertText(insertPlaceholder, insertPosition);

                    // Store the inserted text position.
                    insertedTextPosition = insertPosition.getShiftedBy(0); // Adjust the position.
                });

                // Set the selection to the range after giving some time for the view to update.
                setTimeout(() => {
                    editor.model.change(writer => {
                        // Specify start and end positions for selection
                        let start = 8;
                        let end = 8;

                        // Create a selection range.
                        const rangeStart = insertedTextPosition.getShiftedBy(start);
                        const rangeEnd = insertedTextPosition.getShiftedBy(end);
                        const range = writer.createRange(rangeStart, rangeEnd);

                        writer.setSelection(range);
                    });
                }, 0);
            }
        });

		editor.commands.add('insertRecipient', {
            execute: () => {
                const insertPlaceholder = '{{recipient_name}}';
                const selection = editor.model.document.selection;
                let insertedTextPosition;

                editor.model.change(writer => {
                    const insertPosition = selection.getFirstPosition();

                    // Insert the placeholder at the specified position.
                    writer.insertText(insertPlaceholder, insertPosition);

                    // Store the inserted text position.
                    insertedTextPosition = insertPosition.getShiftedBy(0); // Adjust the position.
                });

                // Set the selection to the range after giving some time for the view to update.
                setTimeout(() => {
                    editor.model.change(writer => {
                        // Specify start and end positions for selection
                        let start = 18;
                        let end = 18;

                        // Create a selection range.
                        const rangeStart = insertedTextPosition.getShiftedBy(start);
                        const rangeEnd = insertedTextPosition.getShiftedBy(end);
                        const range = writer.createRange(rangeStart, rangeEnd);

                        writer.setSelection(range);
                    });
                }, 0);
            }
        });

		editor.commands.add('insertDeadline', {
            execute: () => {
                const insertPlaceholder = '{{deadline}}';
                const selection = editor.model.document.selection;
                let insertedTextPosition;

                editor.model.change(writer => {
                    const insertPosition = selection.getFirstPosition();

                    // Insert the placeholder at the specified position.
                    writer.insertText(insertPlaceholder, insertPosition);

                    // Store the inserted text position.
                    insertedTextPosition = insertPosition.getShiftedBy(0); // Adjust the position.
                });

                // Set the selection to the range after giving some time for the view to update.
                setTimeout(() => {
                    editor.model.change(writer => {
                        // Specify start and end positions for selection
                        let start = 12;
                        let end = 12;

                        // Create a selection range.
                        const rangeStart = insertedTextPosition.getShiftedBy(start);
                        const rangeEnd = insertedTextPosition.getShiftedBy(end);
                        const range = writer.createRange(rangeStart, rangeEnd);

                        writer.setSelection(range);
                    });
                }, 0);
            }
        });

        editor.ui.componentFactory.add( 'placeholderInserter', locale => {

            const dropdown = createDropdown( locale );

            if (!dropdown) {
                throw new Error("Could not create Dropdown");
            }

            const items = new Collection();

			items.add( {
				type: 'button',
				model: new Model( {
					withText: true,
					label: `Url to current survey`,
					commandName: 'insertSelf'
				})
			});

            items.add( {
                type: 'button',
                model: new Model( {
                    withText: true,
                    label: `Link to current survey`,
                    commandName: 'insertSelfLink'
                })
            });

			items.add( {
				type: 'button',
				model: new Model( {
					withText: true,
					label: `Recipient name`,
					commandName: 'insertRecipient'
				})
			});

			items.add( {
				type: 'button',
				model: new Model( {
					withText: true,
					label: `Deadline date`,
					commandName: 'insertDeadline'
				})
			});


            addListToDropdown( dropdown, items );

			dropdown.on('execute', evt => {
                editor.execute(evt.source.commandName, evt.source.commandParams);
                editor.editing.view.focus();
            });

            dropdown.buttonView.set({
                label: 'Placeholders',
                //tooltip: true,
				withText: true
            });

            dropdown.render();

            return dropdown;
        });
    }
}


class Editor extends ClassicEditor {

    static builtinPlugins = [
        Alignment,
        Autoformat,
        BlockQuote,
        Bold,
        SimpleUploadAdapter,
        Essentials,
        FindAndReplace,
        FontBackgroundColor,
        FontColor,
        FontFamily,
        FontSize,
        Heading,
        Highlight,
        HorizontalLine,
        Image,
        ImageCaption,
        ImageResize,
        ImageStyle,
        ImageToolbar,
        ImageUpload,
        ImageInsert,
        Indent,
        Italic,
        Link,
        List,
        Mention,
        // MediaEmbed,
        Paragraph,
        PasteFromOffice,
        Strikethrough,
        Underline,
        Table,
        TableCellProperties,
        TableColumnResize,
        TableProperties,
        TableToolbar,
        TextTransformation,
        DownloadPdf,
        PlaceholderInserter,
        ResizableHeight,
        FullScreen,
        PageBreak,
        ShowBlocks,
        GeneralHtmlSupport,
        Emoji
    ];

    static defaultConfig = {
        licenseKey: 'GPL',
        toolbar: {
            items: [
                'heading',
                '|',
                'placeholderInserter',
                '|',
                'downloadPdf',
                'fontFamily',
                'fontSize',
                'fontBackgroundColor',
                'fontColor',
                'highlight',
                '|',
                'bold',
                'italic',
                'strikethrough',
                'underline',
                'emoji',
                '|',
                'alignment',
                'horizontalLine',
                '|',
                'bulletedList',
                'numberedList',
                'outdent',
                'indent',
                '|',
                'pageBreak',
                'link',
                'imageInsert',
                'blockQuote',
                'insertTable',
                // 'mediaEmbed',
                '|',
                'undo',
                'redo',
                'findAndReplace',
                'fullscreen',
                'showBlocks',
            ],
            shouldNotGroupWhenFull: true
        },
        language: 'en',
        simpleUpload: {
            withCredentials: true,
            headers: {
                'X-CSRF-TOKEN': document.getElementsByTagName('meta')['csrfToken'].getAttribute('content'),
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Headers': 'POST, GET, PUT, DELETE, OPTIONS, HEAD, Authorization, Origin, Accept, X-Requested-With, Content-Type, Access-Control-Request-Method, Access-Control-Request-Headers, Access-Control-Allow-Origin',
                'Access-Control-Allow-Credentials': 'true',
            }
        },
        ResizableHeight: {
            height: '200px',
        },
        image: {
            toolbar: [
                'imageTextAlternative',
                'toggleImageCaption',
                'imageStyle:inline',
                'imageStyle:block',
                'imageStyle:side',
            ]
        },
        table: {
            contentToolbar: [
                'tableColumn',
                'tableRow',
                'mergeTableCells',
                'tableCellProperties',
                'tableProperties'
            ]
        },
        // Explicitly opt into CKEditor's CDN-hosted emoji definitions (the default) so the
        // 'emoji-repository-cdn-use' console warning isn't logged on every load. We don't self-host
        // the emoji dataset, so this makes an outbound request to cdn.ckeditor.com the first time
        // the picker or ':' autocomplete is used in a browser session.
        emoji: {
            definitionsUrl: 'cdn',
        },
    };
}
export default Editor;
